<template>
  <TirPmModal
    classes="task-crop-image-modal"
    :is-visible="isOpened"
    :title="title"
    :is-with-back="false"
    @close="onClose"
  >
    <template #content>
      <div v-if="formData && imageSrc && !isLoadingModal" class="task-crop-image-modal__content">
        <TirPmInput
          v-model="fileName"
          label="Название файла"
          :is-with-hint="fileNameError"
          :is-with-error="fileNameError"
        >
          <template #hintMessage> Недопустимое имя файла </template>
        </TirPmInput>
        <div class="task-crop-image-modal__wrapper-crop">
          <TirPmCropper
            class="task-crop-image-modal__crop"
            v-model="croppedImage"
            :src="imageSrc"
            :min-height="390"
            is-no-aspect
            :aspect="0"
            is-max-default-size
            :show-center-points="true"
            @on-cropped-photo="onCroppedImage"
          />
        </div>
        <TirPmFileUploader
          v-if="!isEditMode"
          class="task-crop-image-modal__replace-image-button"
          :allowed-extensions="['gif', 'jpg', 'jpeg', 'png', 'svg', 'webp']"
          @upload-files="onUploadFiles"
        >
          <TirPmButton
            :variant="TirPmButtonVariantEnum.LinkState"
            :size="TirPmButtonSizeEnum.Small"
          >
            <template #leftSlot>
              <ArrowUpTrayIcon />
            </template>
            Загрузить другое фото
          </TirPmButton>
        </TirPmFileUploader>
        <TirCheckbox
          v-if="!isEditMode"
          v-model="formData.CompressFile"
          class="task-crop-image-modal__checkbox"
        >
          <template #mainSign> Сжать файл </template>
        </TirCheckbox>
      </div>
      <div class="task-crop-image-modal__spinner-wraper" v-else>
        <TirPmSpinner />
      </div>
    </template>
    <template #footer>
      <template v-if="formData && imageSrc && !isLoadingModal">
        <TirPmButton
          :variant="TirPmButtonVariantEnum.PrimaryState"
          :size="TirPmButtonSizeEnum.Regular"
          :is-disabled="fileNameError"
          :is-loading="isLoading"
          @click="onClickConfirm"
        >
          {{ confirmButtonText }}
        </TirPmButton>
        <TirPmButton
          :variant="TirPmButtonVariantEnum.SecondaryState"
          :size="TirPmButtonSizeEnum.Regular"
          @click="onClose"
        >
          Отмена
        </TirPmButton>
      </template>
    </template>
  </TirPmModal>
</template>

<script lang="ts" setup>
import {
  TirPmModal,
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmFileUploader,
  TirPmButtonSizeEnum,
  TirPmInput,
  TirCheckbox,
  TirPmSpinner,
} from "tir-components";
import { ArrowUpTrayIcon } from "tir-style-system/icons/outline";
import { TirPmCropper } from "tir-pm-cropper";
import { computed, PropType, ref } from "vue";
import { TaskCropImageModel } from "./models";
import {
  getBase64FromFile,
  getFileExtension,
  isValidBase64,
  removeFileExtension,
} from "../../utils";

/**Параметры */
const props = defineProps({
  /**Существующие имена файлов */
  existFileNames: { type: Array as PropType<string[]> },
  /**Загрузка */
  isLoading: { type: Boolean, default: false },
});
/**События */
const emit = defineEmits<{
  /**Событие при закрытии модального окна */
  (e: "onClose"): void;
  /**Событие подтверждения */
  (e: "onConfirm", value: TaskCropImageModel): void;
}>();

/**Режим редактирования */
const isEditMode = ref(false);
/**Заголовок */
const title = computed(() => (isEditMode.value ? "Редактирование фото" : "Загрузка фото"));
/**Текст для кнопки подтверждения */
const confirmButtonText = computed(() => (isEditMode.value ? "Применить изменения" : "Сохранить"));
/**Данные формы */
const formData = ref<TaskCropImageModel>();
/**Исходное изображение */
const imageSrc = ref("");
/**Изображение в кроппе */
const croppedImage = ref("");
/**Загрузка модального окна */
const isLoadingModal = ref(false);

/**Название  */
const fileName = computed({
  get() {
    return removeFileExtension(formData.value.FileName);
  },
  set(value) {
    const fileExt = getFileExtension(formData.value.FileName);
    formData.value.FileName = `${value}.${fileExt}`;
  },
});
/**Ошибка с повторяющимся именем файла */
const fileNameError = computed(() => {
  return !fileName.value || props.existFileNames.includes(formData.value.FileName);
});

/**При загрузке файлов */
const onUploadFiles = async (files: Array<File>) => {
  if (!files.length) return;
  const [file] = files;
  const base64 = await getBase64FromFile(file);
  if (!base64) return;

  imageSrc.value = base64;
  croppedImage.value = base64;
  formData.value = new TaskCropImageModel({
    Base64OrUrl: base64,
    FileName: file.name,
    CompressFile: formData.value.CompressFile,
  });
};
/**При кропе изображения */
const onCroppedImage = (image: string, setDefault: boolean = false) => {
  if (setDefault || !isValidBase64(image)) return;
  croppedImage.value = image;
};
/**Открыто ли модальное окно */
const isOpened = computed(() => !!formData.value);
/**Установить изображение */
const setImageSrc = async (base64OrUrl: string) => {
  // Проверяем, является ли строка URL
  if (base64OrUrl.includes("http")) {
    try {
      const response = await fetch(base64OrUrl);
      if (response.ok) {
        const blob = await response.blob();
        const reader = new FileReader();

        // Конвертируем Blob в base64
        reader.onloadend = () => {
          const base64data = reader.result as string;
          imageSrc.value = base64data;
          croppedImage.value = base64data;
        };
        reader.readAsDataURL(blob);
      }
    } catch (e) {
      console.error(e);
    }
  } else {
    if (base64OrUrl.startsWith("data:image")) {
      imageSrc.value = base64OrUrl;
      croppedImage.value = base64OrUrl;
    }
  }
};
/**Открыть модальное окно */
const open = async (data: TaskCropImageModel, _isEditMode: boolean) => {
  isEditMode.value = _isEditMode;
  isLoadingModal.value = true;
  formData.value = data;
  await setImageSrc(data.Base64OrUrl);
  isLoadingModal.value = false;
};
/**Закрыть модальное окно */
const close = () => {
  croppedImage.value = "";
  formData.value = undefined;
};
/**При закрытии модального окна */
const onClose = () => {
  close();
  emit("onClose");
};
/**Кликн на кнопку подтверждения */
const onClickConfirm = () => {
  emit(
    "onConfirm",
    new TaskCropImageModel({
      Base64OrUrl: croppedImage.value,
      FileName: formData.value.FileName,
      CompressFile: formData.value.CompressFile,
    }),
  );
};

defineExpose({
  open,
  close,
});
</script>

<style lang="scss" scoped>
.task-crop-image-modal {
  &__wrapper-crop {
    height: 24.375rem;
    margin-top: 1rem;
  }
  &__replace-image-button {
    margin-top: 1rem;
  }
  &__checkbox {
    margin-top: 1rem;
  }
  &__spinner-wraper {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 31.875rem;
  }
}
</style>
<style lang="scss">
.task-crop-image-modal__crop {
  border-radius: var(--8, 0.5rem);
  overflow: hidden;
  .tir-pm-cropper-container__controls {
    display: none;
  }
  .tir-pm-cropper-container__controls-size {
    display: none;
  }
}
</style>
